/* The worker half: ONE MAILBOX, ONE WORKER. Everything that touches the file
 * happens here, off the page's thread, so a multi gigabyte archive never
 * freezes the page. Closing a mailbox terminates its worker, which is what
 * frees its memory.
 *
 * **The file is never read whole.** Opening reads it in chunks and keeps where
 * each message starts and ends plus a summary of its headers; a message is
 * decoded only when it is opened, printed or searched.
 *
 * Messages in:  open {file} | get {id, req} | raw {id, req} | search {query, generation}
 * Messages out: batch | progress | done | message | raw | searchProgress | searchDone | error
 */

import PostalMime from "./vendor/postal-mime/postal-mime.js";
import { nextLine, isSeparator, headerBlock, stripEnvelope, summarise } from "./engine/mbox.js";
import { fold, searchableText, matchesAll } from "./engine/text.js";

const DEFAULTS = {
  chunk: 16 * 1024 * 1024,   // bytes read per step of the index pass
  headerLimit: 256 * 1024,   // a header block longer than this is read on its own
  textLimit: 100_000_000,    // characters of searchable text one mailbox may cache
  reportEveryMs: 200,        // how often a search reports and lets a newer query in
  batch: 2000,               // summaries per message to the page
};

/**
 * Starts serving on `scope` (the worker's `self`, or a test's stand in).
 * `options` exists for the tests: a tiny `chunk` is what puts every boundary
 * case in reach of an eleven kilobyte fixture.
 */
export function serve(scope, options = {}) {
  const settings = { ...DEFAULTS, ...options };
  const state = { file: null, starts: [], ends: [], texts: [], textChars: 0, generation: 0 };
  const post = (message, transfer) => scope.postMessage(message, transfer || []);

  scope.onmessage = async ({ data }) => {
    try {
      switch (data.type) {
        case "open": return await open(data.file);
        case "get": return await get(data.id, data.req);
        case "raw": return await raw(data.id, data.req);
        case "search": return await search(data.query, data.generation);
      }
    } catch (error) {
      post({ type: "error", req: data.req, message: String(error?.stack || error) });
    }
  };

  async function read(from, to) {
    return new Uint8Array(await state.file.slice(from, to).arrayBuffer());
  }

  // ------------------------------------------------------------ the index

  async function open(file) {
    Object.assign(state, { file, starts: [], ends: [], texts: [], textChars: 0 });
    const began = performance.now();
    let batch = [];
    const late = []; // messages whose header did not fit in the chunk that found them
    let current = null; // the message found but not yet reported: its end is the next start

    const flush = () => {
      if (batch.length) {
        post({ type: "batch", items: batch });
        batch = [];
      }
    };

    const finish = (end) => {
      if (!current) return;
      state.ends[current.id] = end;
      if (current.header) batch.push(summarise(current.id, current.header, end - state.starts[current.id]));
      else late.push(current.id);
      current = null;
      if (batch.length >= settings.batch) flush();
    };

    for (let offset = 0; offset < file.size; offset += settings.chunk) {
      // A header's worth of overlap, so most header blocks are whole in the
      // chunk that finds them and "\nFrom " cannot straddle a boundary unseen.
      const bytes = await read(offset, offset + settings.chunk + settings.headerLimit);
      const scanEnd = Math.min(bytes.length, settings.chunk);

      let i = 0;
      if (offset > 0) {
        const [before] = await read(offset - 1, offset);
        if (before !== 0x0a) i = nextLine(bytes, 0);
      }

      for (; i >= 0 && i < scanEnd; i = nextLine(bytes, i)) {
        if (!isSeparator(bytes, i)) continue;
        finish(offset + i);
        current = { id: state.starts.length, header: headerBlock(bytes, i, settings.headerLimit) };
        state.starts.push(offset + i);
      }

      flush();
      post({ type: "progress", bytes: Math.min(offset + settings.chunk, file.size), total: file.size, count: state.starts.length });
    }

    finish(file.size);

    for (const id of late) {
      const bytes = await read(state.starts[id], Math.min(state.ends[id], state.starts[id] + settings.headerLimit));
      batch.push(summarise(id, headerBlock(bytes, 0, settings.headerLimit, true), state.ends[id] - state.starts[id]));
      if (batch.length >= settings.batch) flush();
    }

    flush();
    state.texts = new Array(state.starts.length);
    post({ type: "done", count: state.starts.length, bytes: file.size, ms: Math.round(performance.now() - began) });
  }

  // ------------------------------------------------------------ one message

  async function messageBytes(id) {
    return stripEnvelope(await read(state.starts[id], state.ends[id]));
  }

  async function get(id, req) {
    const parsed = await PostalMime.parse(await messageBytes(id), { attachmentEncoding: "arraybuffer" });
    const transfer = parsed.attachments.map((a) => a.content).filter((c) => c instanceof ArrayBuffer);
    post({ type: "message", req, id, parsed }, transfer);
  }

  async function raw(id, req) {
    const bytes = await messageBytes(id);
    const copy = bytes.slice(); // its own buffer, so exactly these bytes are transferred
    post({ type: "raw", req, id, bytes: copy }, [copy.buffer]);
  }

  // ------------------------------------------------------------ searching

  async function textOf(id, bytes) {
    if (state.texts[id] !== undefined) return state.texts[id];

    let text = "";
    try {
      text = searchableText(await PostalMime.parse(stripEnvelope(bytes), { attachmentEncoding: "arraybuffer" }));
    } catch {
      text = ""; // a message postal-mime cannot parse matches nothing rather than stopping the search
    }

    if (state.textChars + text.length <= settings.textLimit) {
      state.texts[id] = text;
      state.textChars += text.length;
    }

    return text;
  }

  /**
   * Every term must appear in the message's text or headers.
   *
   * **Reports and yields on a clock, not per chunk.** A chunk can take a second
   * or more to decode, which made hits land in bursts, and a worker only
   * receives the page's messages when its loop hands control back, so a newer
   * query could not cancel this one until the chunk ended. Every
   * `reportEveryMs` it posts what it has found and waits a macrotask; a newer
   * `search` sets a new generation, and this one sees it and stops.
   */
  async function search(query, generation) {
    state.generation = generation;
    const terms = fold(query || "").split(/\s+/).filter(Boolean);
    const total = state.starts.length;

    if (!terms.length || !state.file) {
      post({ type: "searchDone", generation, ms: 0, found: 0 });
      return;
    }

    const began = performance.now();
    let hits = [];
    let found = 0;
    let lastReport = performance.now();

    for (let id = 0; id < total;) {
      // A run of consecutive messages up to one chunk, read in one slice.
      let last = id;
      while (last + 1 < total && state.ends[last + 1] - state.starts[id] <= settings.chunk) last++;

      let needsRead = false;
      for (let m = id; m <= last && !needsRead; m++) needsRead = state.texts[m] === undefined;
      const run = needsRead ? await read(state.starts[id], state.ends[last]) : null;

      for (let m = id; m <= last; m++) {
        const text = state.texts[m] ?? (await textOf(m, run.subarray(state.starts[m] - state.starts[id], state.ends[m] - state.starts[id])));

        if (matchesAll(text, terms)) {
          hits.push(m);
          found++;
        }

        if (performance.now() - lastReport >= settings.reportEveryMs) {
          post({ type: "searchProgress", generation, done: m + 1, total, hits, found });
          hits = [];
          await new Promise((resolve) => setTimeout(resolve, 0));
          if (generation !== state.generation) return;
          lastReport = performance.now();
        }
      }

      id = last + 1;
      if (generation !== state.generation) return;
    }

    post({ type: "searchProgress", generation, done: total, total, hits, found });
    post({ type: "searchDone", generation, found, ms: Math.round(performance.now() - began) });
  }
}
